import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import useConversationService from '../../app/services/hooks/useConversationService';
import { Conversation, Message, Contact } from '../../app/models/Conversation';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import './ChatWidget.css';

const POLLING_INTERVAL = 10000; // 10 segundos

const ChatWidget: React.FC = () => {
    const { auth } = useContext(AuthContext);
    const {
        fetchingConversations,
        fetchingMessages,
        fetchingSendMessage,
        fetchingChatAI,
        getConversations,
        getMessages,
        sendMessage,
        markMessagesAsRead,
        getUnreadCount,
        chatWithAI
    } = useConversationService();

    const [isOpen, setIsOpen] = useState(false);
    const [activeView, setActiveView] = useState<'list' | 'chat'>('list');
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [messageInput, setMessageInput] = useState('');
    const [totalUnread, setTotalUnread] = useState(0);
    const [newestMessageId, setNewestMessageId] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
    
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const currentUserId = auth?.id;
    
    // Constantes para validación de archivos
    const MAX_FILE_SIZE_MB = 10;
    const ALLOWED_FILE_TYPES = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'text/plain'
    ];

    // Cargar conversaciones al abrir
    useEffect(() => {
        if (isOpen && auth) {
            loadConversations();
        }
    }, [isOpen, auth]);

    // Polling para mensajes nuevos
    useEffect(() => {
        if (isOpen && activeConversation && newestMessageId) {
            pollingRef.current = setInterval(() => {
                pollNewMessages();
            }, POLLING_INTERVAL);

            return () => {
                if (pollingRef.current) {
                    clearInterval(pollingRef.current);
                }
            };
        }
    }, [isOpen, activeConversation, newestMessageId]);

    // Scroll al último mensaje
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const loadConversations = () => {
        getConversations({
            onSuccess: (response: ServiceResponse) => {
                setConversations(response.data.conversations || []);
                setTotalUnread(response.data.total_unread || 0);
            }
        });
    };

    const loadMessages = (conversationId: number) => {
        getMessages(conversationId, { limit: 50 }, {
            onSuccess: (response: ServiceResponse) => {
                setMessages(response.data.messages || []);
                if (response.data.newest_id) {
                    setNewestMessageId(response.data.newest_id);
                }
            }
        });
    };

    const pollNewMessages = () => {
        if (!activeConversation || !newestMessageId) return;

        getMessages(activeConversation.id, { afterId: newestMessageId }, {
            onSuccess: (response: ServiceResponse) => {
                const newMessages = response.data.messages || [];
                if (newMessages.length > 0) {
                    setMessages(prev => [...prev, ...newMessages]);
                    setNewestMessageId(response.data.newest_id);
                    // Actualizar conversaciones para reflejar nuevos mensajes
                    loadConversations();
                }
            }
        });
    };

    const handleOpenConversation = (conversation: Conversation) => {
        setActiveConversation(conversation);
        setActiveView('chat');
        loadMessages(conversation.id);
        
        // Marcar como leído
        if (conversation.unread_count > 0) {
            markMessagesAsRead(conversation.id, {
                onSuccess: () => {
                    loadConversations();
                }
            });
        }
    };

    const handleBackToList = () => {
        setActiveView('list');
        setActiveConversation(null);
        setMessages([]);
        setNewestMessageId(null);
        setSelectedDocument(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        loadConversations();
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validar tamaño
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            alert(`El archivo es demasiado grande. Tamaño máximo: ${MAX_FILE_SIZE_MB} MB`);
            e.target.value = '';
            return;
        }

        // Validar tipo
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            alert('Tipo de archivo no permitido. Formatos aceptados: PDF, DOCX, DOC, TXT');
            e.target.value = '';
            return;
        }

        setSelectedDocument(file);
    };

    const handleRemoveDocument = () => {
        setSelectedDocument(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSendMessage = () => {
        if (!messageInput.trim() || !activeConversation) return;

        const content = messageInput.trim();
        
        // Preparar contenido del mensaje para mostrar
        let displayContent = content;
        if (selectedDocument) {
            displayContent = `[Documento: ${selectedDocument.name}]\n\n${content}`;
        }
        
        // Guardar referencia al documento antes de limpiar
        const documentToSend = selectedDocument;
        
        setMessageInput('');
        setSelectedDocument(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        // Si es conversación con IA
        if (activeConversation.is_ai) {
            // Agregar mensaje del usuario localmente
            const tempUserMessage: Message = {
                id: Date.now(),
                conversation_id: activeConversation.id,
                sender_id: currentUserId || 0,
                sender_name: 'Tú',
                sender_avatar: null,
                sender_initials: 'TU',
                message_type: 'text',
                content: displayContent,
                attachment_url: null,
                attachment_name: '',
                is_ai_message: false,
                is_read: true,
                read_at: null,
                created_at: new Date().toISOString()
            };
            setMessages(prev => [...prev, tempUserMessage]);

            chatWithAI(content, documentToSend || undefined, {
                onSuccess: (response: ServiceResponse) => {
                    const aiMessage = response.data.ai_message;
                    setMessages(prev => {
                        // Reemplazar el mensaje temporal con el real
                        const withoutTemp = prev.filter(m => m.id !== tempUserMessage.id);
                        return [...withoutTemp, response.data.user_message, aiMessage];
                    });
                    setNewestMessageId(aiMessage.id);
                    loadConversations();
                },
                onError: () => {
                    // Mantener el mensaje del usuario pero agregar mensaje de error
                    const errorMessage: Message = {
                        id: Date.now() + 1,
                        conversation_id: activeConversation.id,
                        sender_id: 'ai',
                        sender_name: 'Asistente Monere',
                        sender_avatar: null,
                        sender_initials: 'IA',
                        message_type: 'text',
                        content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor intenta de nuevo.',
                        attachment_url: null,
                        attachment_name: '',
                        is_ai_message: true,
                        is_read: true,
                        read_at: null,
                        created_at: new Date().toISOString()
                    };
                    setMessages(prev => [...prev, errorMessage]);
                }
            });
        } else {
            // Conversación directa
            sendMessage(
                { conversation_id: activeConversation.id, content },
                {
                    onSuccess: (response: ServiceResponse) => {
                        const newMessage = response.data.message;
                        setMessages(prev => [...prev, newMessage]);
                        setNewestMessageId(newMessage.id);
                        loadConversations();
                    }
                }
            );
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const getInitials = (name: string) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const formatTime = (dateString: string | null) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) {
            return date.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
        } else if (days === 1) {
            return 'Ayer';
        } else if (days < 7) {
            return date.toLocaleDateString('es-CL', { weekday: 'short' });
        } else {
            return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' });
        }
    };

    const filteredConversations = conversations.filter(conv => {
        if (!searchTerm) return true;
        const name = conv.other_user_name || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    // Separar conversación con IA (sticky)
    const aiConversation = conversations.find(c => c.is_ai);
    const directConversations = filteredConversations.filter(c => !c.is_ai);

    if (!auth) return null;

    return (
        <div className={`chat-widget-container ${isOpen ? 'chat-open' : ''}`}>
            {/* Tab lateral */}
            {!isOpen && (
                <button 
                    className="chat-widget-trigger"
                    onClick={() => setIsOpen(true)}
                >
                    <i className="fas fa-comments"></i>
                    <span className="chat-widget-trigger-label">Chat</span>
                    {totalUnread > 0 && (
                        <span className="chat-widget-badge">{totalUnread}</span>
                    )}
                </button>
            )}

            {/* Ventana de chat */}
            {isOpen && (
                <div className="chat-widget-window">
                    {/* Header */}
                    <div className="chat-widget-header">
                        {activeView === 'chat' && activeConversation ? (
                            <>
                                <button className="chat-back-btn" onClick={handleBackToList}>
                                    <i className="fas fa-arrow-left"></i>
                                </button>
                                <div className="chat-header-info">
                                    <div className={`chat-header-avatar ${activeConversation.is_ai ? 'ai-avatar' : ''}`}>
                                        {activeConversation.is_ai ? (
                                            <i className="fas fa-robot"></i>
                                        ) : (
                                            activeConversation.other_user_initials || getInitials(activeConversation.other_user_name || '')
                                        )}
                                    </div>
                                    <div className="chat-header-details">
                                        <span className="chat-header-name">
                                            {activeConversation.other_user_name || 'Asistente Monere'}
                                        </span>
                                        <span className="chat-header-status">
                                            {activeConversation.is_ai ? 'Siempre disponible' : (activeConversation.context_label || 'En línea')}
                                        </span>
                                    </div>
                                </div>
                                <button className="chat-close-btn" onClick={() => setIsOpen(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="chat-header-title">
                                    <i className="fas fa-comments"></i>
                                    <span>Mensajes</span>
                                </div>
                                <button className="chat-close-btn" onClick={() => setIsOpen(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </>
                        )}
                    </div>

                    {/* Contenido */}
                    <div className="chat-widget-content">
                        {activeView === 'list' ? (
                            /* Lista de conversaciones */
                            <div className="chat-conversations-list">
                                {/* Chat con IA - Sticky */}
                                {aiConversation && (
                                    <div 
                                        className="chat-conversation-item ai-conversation sticky"
                                        onClick={() => handleOpenConversation(aiConversation)}
                                    >
                                        <div className="conversation-avatar ai-avatar">
                                            <i className="fas fa-robot"></i>
                                        </div>
                                        <div className="conversation-details">
                                            <div className="conversation-header">
                                                <span className="conversation-name">
                                                    Asistente Monere
                                                    <span className="ai-badge">IA</span>
                                                </span>
                                            </div>
                                            <div className="conversation-preview">
                                                <span className="conversation-message">
                                                    {aiConversation.last_message_preview || '¡Hola! ¿En qué puedo ayudarte?'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Separador */}
                                {directConversations.length > 0 && (
                                    <div className="conversations-divider">
                                        <span>Conversaciones</span>
                                    </div>
                                )}

                                {/* Búsqueda */}
                                <div className="chat-search-container">
                                    <i className="fas fa-search"></i>
                                    <input 
                                        type="text" 
                                        placeholder="Buscar conversación..." 
                                        className="chat-search-input"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>

                                {/* Conversaciones directas */}
                                {fetchingConversations ? (
                                    <div className="chat-loading">
                                        <i className="fas fa-spinner fa-spin"></i>
                                        <span>Cargando...</span>
                                    </div>
                                ) : directConversations.length === 0 ? (
                                    <div className="chat-empty">
                                        <i className="fas fa-comments"></i>
                                        <span>No hay conversaciones</span>
                                        <small>Las conversaciones aparecerán cuando tengas solicitudes con abogados o clientes asignados</small>
                                    </div>
                                ) : (
                                    directConversations.map(conversation => (
                                        <div 
                                            key={conversation.id}
                                            className={`chat-conversation-item ${conversation.unread_count > 0 ? 'unread' : ''}`}
                                            onClick={() => handleOpenConversation(conversation)}
                                        >
                                            <div className="conversation-avatar">
                                                {conversation.other_user_initials || getInitials(conversation.other_user_name || '')}
                                            </div>
                                            <div className="conversation-details">
                                                <div className="conversation-header">
                                                    <span className="conversation-name">{conversation.other_user_name}</span>
                                                    <span className="conversation-time">
                                                        {formatTime(conversation.last_message_at)}
                                                    </span>
                                                </div>
                                                <div className="conversation-preview">
                                                    <span className="conversation-message">
                                                        {conversation.last_message_preview || 'Sin mensajes'}
                                                    </span>
                                                    {conversation.unread_count > 0 && (
                                                        <span className="conversation-badge">{conversation.unread_count}</span>
                                                    )}
                                                </div>
                                                {conversation.context_label && (
                                                    <div className="conversation-context">
                                                        <i className="fas fa-tag"></i>
                                                        <span>{conversation.context_label}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        ) : (
                            /* Chat activo */
                            <div className="chat-messages-container">
                                <div className="chat-messages">
                                    {fetchingMessages && messages.length === 0 ? (
                                        <div className="chat-loading">
                                            <i className="fas fa-spinner fa-spin"></i>
                                            <span>Cargando mensajes...</span>
                                        </div>
                                    ) : messages.length === 0 ? (
                                        <div className="chat-empty-messages">
                                            <i className={activeConversation?.is_ai ? 'fas fa-robot' : 'fas fa-comments'}></i>
                                            <span>
                                                {activeConversation?.is_ai 
                                                    ? '¡Hola! Soy el asistente de Monere. ¿En qué puedo ayudarte?' 
                                                    : 'No hay mensajes aún. ¡Inicia la conversación!'}
                                            </span>
                                        </div>
                                    ) : (
                                        messages.map((message) => {
                                            const isSent = !message.is_ai_message && message.sender_id === currentUserId;
                                            return (
                                                <div 
                                                    key={message.id} 
                                                    className={`chat-message ${isSent ? 'sent' : 'received'} ${message.is_ai_message ? 'ai-message' : ''}`}
                                                >
                                                    {!isSent && (
                                                        <div className={`message-avatar ${message.is_ai_message ? 'ai-avatar' : ''}`}>
                                                            {message.is_ai_message ? (
                                                                <i className="fas fa-robot"></i>
                                                            ) : (
                                                                message.sender_initials
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="message-bubble">
                                                        <p className="message-text">{message.content}</p>
                                                        <span className="message-time">
                                                            {formatTime(message.created_at)}
                                                            {isSent && (
                                                                <i className={`fas fa-check-double ${message.is_read ? 'read' : ''}`}></i>
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Documento seleccionado - Banner flotante (solo para IA) */}
                    {activeView === 'chat' && selectedDocument && activeConversation?.is_ai && (
                        <div className="chat-document-banner">
                            <div className="chat-document-banner-icon">
                                <i className="fas fa-file-alt"></i>
                            </div>
                            <div className="chat-document-banner-info">
                                <span className="chat-document-banner-label">Documento para análisis:</span>
                                <span className="chat-document-banner-name">{selectedDocument.name}</span>
                            </div>
                            <button 
                                className="chat-document-banner-remove"
                                onClick={handleRemoveDocument}
                                title="Quitar documento"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    )}

                    {/* Input de mensaje (solo en vista de chat) */}
                    {activeView === 'chat' && (
                        <div className="chat-input-container">
                            <div className="chat-input-row">
                                {/* Input oculto para archivo */}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    style={{ display: 'none' }}
                                    accept=".pdf,.doc,.docx,.txt"
                                    onChange={handleFileSelect}
                                />
                                
                                {/* Botón adjuntar (solo para IA) */}
                                {activeConversation?.is_ai && (
                                    <button 
                                        className={`chat-attach-btn ${selectedDocument ? 'has-file' : ''}`}
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={fetchingSendMessage || fetchingChatAI}
                                        title="Puedes adjuntar PDF, DOCX o TXT para análisis legal"
                                    >
                                        <i className="fas fa-paperclip"></i>
                                    </button>
                                )}
                                
                                <div className="chat-input-wrapper">
                                    <textarea
                                        value={messageInput}
                                        onChange={(e) => setMessageInput(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder={selectedDocument 
                                            ? "¿Qué quieres que analice del documento?" 
                                            : (activeConversation?.is_ai 
                                                ? "Pregunta sobre nuestros servicios..." 
                                                : "Escribe un mensaje...")}
                                        rows={1}
                                        className="chat-input"
                                        disabled={fetchingSendMessage || fetchingChatAI}
                                    />
                                </div>
                                <button 
                                    className={`chat-send-btn ${messageInput.trim() ? 'active' : ''}`}
                                    onClick={handleSendMessage}
                                    disabled={!messageInput.trim() || fetchingSendMessage || fetchingChatAI}
                                >
                                    {(fetchingSendMessage || fetchingChatAI) ? (
                                        <i className="fas fa-spinner fa-spin"></i>
                                    ) : (
                                        <i className="fas fa-paper-plane"></i>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ChatWidget;
